// frontend/src/components/TemplateGenerationModal.tsx
/**
 * Modal component for displaying AI template generation progress.
 *
 * Shows real-time progress as Celery processes categories in the background.
 * Displays current phase, questions scored, categories completed, and activity feed.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Paper,
  List,
  ListItem,
  Fade,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Category as CategoryIcon,
  QuestionAnswer as QuestionAnswerIcon,
} from '@mui/icons-material';
import { auditAPI } from '../services/api';

// Types for template generation status
interface ActivityItem {
  timestamp: string;
  type: 'category' | 'question' | 'phase';
  message: string;
  details?: {
    question?: string;
    answers?: string[];
    categories?: string[];
    total_questions?: number;
    total_categories?: number;
  };
}

interface TemplateGenerationStatus {
  session_id: string;
  status: 'generating' | 'processing' | 'preview' | 'completed' | 'failed' | 'cancelled';
  current_phase: string;
  total_categories: number;
  categories_completed: number;
  questions_generated: number;
  target_question_count?: number;
  progress_percentage: number;
  is_complete: boolean;
  error_message: string | null;
  template_preview: any | null;
  created_template_id?: number | null;
  recent_activity: ActivityItem[];
}

/** True when AI structure is ready for the review step (preview or draft already saved). */
const isTemplateReady = (status: TemplateGenerationStatus | null): boolean =>
  !!status?.is_complete &&
  (status.status === 'preview' || status.status === 'completed') &&
  !!status.template_preview;

interface TemplateGenerationModalProps {
  open: boolean;
  sessionId: string | null;
  onClose: () => void;
  onComplete: (template: any) => void;
}

const TemplateGenerationModal: React.FC<TemplateGenerationModalProps> = ({
  open,
  sessionId,
  onClose,
  onComplete,
}) => {
  const [status, setStatus] = useState<TemplateGenerationStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track if we've already completed to prevent duplicate onComplete calls
  const hasCompletedRef = useRef(false);

  // Poll for status updates
  const pollStatus = useCallback(async () => {
    if (!sessionId || sessionId === 'connecting') return; // Don't poll if waiting for backend

    try {
      const data = await auditAPI.getTemplateGenerationProgress(sessionId);
      console.log('[DEBUG] Poll status response:', data);
      setStatus(data);
      setError(null);

      // Stop polling when complete (the useEffect will handle auto-close)
      if (data.is_complete) {
        setIsPolling(false);
      }
    } catch (err: any) {
      console.error('[ERROR] Polling status failed:', err);
      setError(err.response?.data?.error || 'Failed to get status');
      setIsPolling(false);
    }
  }, [sessionId]);

  // Auto-close modal when template is complete
  // Use ref to prevent duplicate calls if parent re-renders with new callback references
  useEffect(() => {
    console.log('[DEBUG] Auto-close effect triggered:', {
      is_complete: status?.is_complete,
      status: status?.status,
      has_preview: !!status?.template_preview,
      already_completed: hasCompletedRef.current
    });

    // Handle both 'preview' (structure ready) and 'completed' (draft already saved).
    // Draft save promotes preview → completed; UI must treat both as success.
    if (isTemplateReady(status) && !hasCompletedRef.current) {
      console.log('[OK] Template ready - auto-closing modal and showing template');
      hasCompletedRef.current = true;
      onComplete(status!.template_preview);
      onClose();
    }
  }, [status, onComplete, onClose]);

  // Start polling when modal opens with a session
  useEffect(() => {
    if (open && sessionId && !status?.is_complete) {
      // Reset completion flag when modal reopens
      hasCompletedRef.current = false;
      setIsPolling(true);
      pollStatus(); // Initial fetch
    }

    return () => {
      setIsPolling(false);
    };
  }, [open, sessionId, pollStatus, status?.is_complete]);

  // Polling interval — 1.5s so question-level scoring updates feel live
  useEffect(() => {
    if (!isPolling) return;

    const interval = setInterval(pollStatus, 1500);

    return () => clearInterval(interval);
  }, [isPolling, pollStatus]);

  // Get status icon and color
  const getStatusDisplay = () => {
    // Special case: Waiting for backend to respond
    if (!status || sessionId === 'connecting') {
      return {
        icon: <CircularProgress size={24} />,
        color: 'info' as const,
        text: 'Connecting to server...',
        subtitle: 'This may take a moment if server is starting up'
      };
    }

    switch (status.status) {
      case 'generating':
        return { icon: <CircularProgress size={24} />, color: 'info' as const, text: 'Planning template structure...' };
      case 'processing':
        return { icon: <CircularProgress size={24} />, color: 'info' as const, text: status.current_phase || 'Generating questions...' };
      case 'preview':
      case 'completed':
        return { icon: <CheckCircleIcon color="success" />, color: 'success' as const, text: 'Template generated successfully!' };
      case 'failed':
        return { icon: <ErrorIcon color="error" />, color: 'error' as const, text: 'Generation failed' };
      case 'cancelled':
        return { icon: <ErrorIcon color="warning" />, color: 'warning' as const, text: 'Generation cancelled' };
      default:
        return { icon: <CircularProgress size={24} />, color: 'info' as const, text: 'Processing...' };
    }
  };

  const statusDisplay = getStatusDisplay();
  const targetQuestions = status?.target_question_count || 0;
  const questionsDone = status?.questions_generated || 0;
  const progressLabel =
    targetQuestions > 0
      ? `${questionsDone} of ${targetQuestions} questions`
      : status?.total_categories
        ? `${status.categories_completed} of ${status.total_categories} categories`
        : 'Starting...';

  return (
    <Dialog
      open={open}
      onClose={status?.is_complete ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!status?.is_complete}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {statusDisplay.icon}
        <span>AI Template Generation</span>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          {/* Status message */}
          <Typography variant="body1" gutterBottom>
            {statusDisplay.text}
          </Typography>

          {/* Subtitle for connecting state */}
          {(statusDisplay as any).subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              {(statusDisplay as any).subtitle}
            </Typography>
          )}

          {/* Live counters while generating */}
          {status && !status.is_complete && sessionId !== 'connecting' && (
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
              <Chip
                size="small"
                icon={<QuestionAnswerIcon />}
                label={
                  targetQuestions > 0
                    ? `${questionsDone} / ${targetQuestions} questions scored`
                    : `${questionsDone} questions scored`
                }
                color="primary"
                variant="outlined"
              />
              {status.total_categories > 0 && (
                <Chip
                  size="small"
                  icon={<CategoryIcon />}
                  label={`${status.categories_completed} / ${status.total_categories} categories done`}
                  variant="outlined"
                />
              )}
            </Stack>
          )}

          {/* Progress bar */}
          {status && !status.is_complete && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Progress
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  {progressLabel} ({status.progress_percentage}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={status.progress_percentage}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}

          {/* Activity Feed */}
          {status && !status.is_complete && status.recent_activity && status.recent_activity.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                mb: 2,
                maxHeight: 300,
                overflow: 'auto',
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Live activity
                </Typography>
              </Box>
              <List dense sx={{ py: 1 }}>
                {status.recent_activity.map((activity, index) => (
                  <Fade in key={`${activity.timestamp}-${index}`} timeout={500}>
                    <ListItem sx={{ py: 0.5 }}>
                      <Box sx={{ width: '100%' }}>
                        {/* Phase activity: Category headers or milestones */}
                        {activity.type === 'phase' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <CategoryIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="body2" fontWeight={600} color="primary.main">
                              {activity.message}
                            </Typography>
                          </Box>
                        )}

                        {/* Question activity: Individual questions (indented) */}
                        {activity.type === 'question' && (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, ml: 2, mb: 0.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main', mt: 0.25 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                              {activity.message}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                  </Fade>
                ))}
              </List>
            </Paper>
          )}

          {/* Completion stats */}
          {isTemplateReady(status) && (
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${status!.questions_generated} questions generated`}
                color="success"
                variant="outlined"
              />
              <Chip
                icon={<CheckCircleIcon />}
                label={`${status!.total_categories} categories`}
                color="primary"
                variant="outlined"
              />
            </Stack>
          )}

          {/* Error message */}
          {(error || status?.error_message) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error || status?.error_message}
            </Alert>
          )}

          {/* Safe to leave message */}
          {status && !status.is_complete && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This process runs in the background. You can safely close this dialog -
              generation will continue and you can check progress later.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {/* Close/Continue button */}
        <Button
          onClick={() => {
            // If template is complete and successful, trigger onComplete before closing
            // Check hasCompletedRef to prevent duplicate calls if auto-close effect already fired
            if (isTemplateReady(status) && !hasCompletedRef.current) {
              hasCompletedRef.current = true;
              onComplete(status!.template_preview);
            }
            onClose();
          }}
          variant={status?.is_complete ? 'contained' : 'outlined'}
          startIcon={status?.is_complete ? <CheckCircleIcon /> : <CloseIcon />}
          color={isTemplateReady(status) ? 'success' : 'primary'}
          data-testid="template-generation-modal-action-button"
        >
          {isTemplateReady(status) ? 'View Template' : status?.is_complete ? 'Close' : 'Continue in Background'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateGenerationModal;
